/**
 * Settings → Workers — Add Worker default values.
 *
 * P1.2 Phase B: lets the org admin configure the four ID defaults
 * the Add Worker dialog pre-fills:
 *
 *   * default_department_id
 *   * default_position_id
 *   * default_location_id
 *   * default_employment_type_id
 *
 * Each is independently optional. The ActiveWorkers page reads
 * /v1/system/settings/workers on mount and only pre-fills fields
 * whose configured ID still resolves in the dropdown — so a stale
 * configuration here never strands the form.
 *
 * Persistence model: a single JSONB blob under the 'workers' Setting
 * category (no migration needed; the system_settings table is
 * (org_id, category) → JSONB by design). PATCH semantics merge,
 * so the four fields can be edited independently without clobbering
 * each other.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { ApiError, organizationApi, systemApi } from '../../../api';
import type { WorkersSettingsData } from '../../../api/system';
import { translateApiError } from '../../../i18n/translateApiError';

interface CatalogOption {
  id: string;
  name?: string;
  title?: string;
}

const Workers: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<WorkersSettingsData>({});
  const [departments, setDepartments] = useState<CatalogOption[]>([]);
  const [positions, setPositions] = useState<CatalogOption[]>([]);
  const [locations, setLocations] = useState<CatalogOption[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<CatalogOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Catalog data populates the four selects; settings.data carries
      // whichever subset of defaults the admin has already configured.
      const [deps, poss, locs, types, settings] = await Promise.all([
        organizationApi.departmentsApi.list(),
        organizationApi.positionsApi.list(),
        organizationApi.locationsApi.list(),
        organizationApi.employmentTypesApi.list(),
        systemApi.systemSettingsApi.getWorkers().catch(() => null),
      ]);
      setDepartments(deps as CatalogOption[]);
      setPositions(poss as CatalogOption[]);
      setLocations(locs as CatalogOption[]);
      setEmploymentTypes(types as CatalogOption[]);
      setData((settings?.data as WorkersSettingsData) ?? {});
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:shared.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const set = <K extends keyof WorkersSettingsData>(
    key: K,
    value: WorkersSettingsData[K],
  ) => setData((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      // PATCH merges, but we always send the full known set so an
      // explicit "clear default" (empty string from the picker) is
      // recognised as null on the server. Empty strings → null
      // because the JSONB schema treats missing/null identically and
      // the Add Worker dialog's existence check prefers null.
      const normalized: WorkersSettingsData = {
        default_department_id: data.default_department_id || null,
        default_position_id: data.default_position_id || null,
        default_location_id: data.default_location_id || null,
        default_employment_type_id: data.default_employment_type_id || null,
      };
      await systemApi.systemSettingsApi.updateWorkers(normalized);
      setToast(t('system:shared.savedToast', { module: 'Worker defaults' }));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('system:shared.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('system:workers.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('system:workers.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : <Save />
          }
          onClick={save}
          disabled={saving}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
        >
          {saving ? t('system:shared.saving') : t('system:shared.saveChanges')}
        </Button>
      </Box>

      <Alert icon={false} severity="info" sx={{ mb: 2, width: '100%' }}>
        {t('system:workers.infoAlert')}
      </Alert>

      <Grid container spacing={2} sx={{ alignSelf: 'stretch' }}>
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
          >
            <Box sx={{ px: 2.5, py: 1.25 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: '0.9rem' }}
              >
                {t('system:workers.cardOrganisation')}
              </Typography>
            </Box>
            <CardContent sx={{ px: 2.5, pt: 0.75, pb: 2 }}>
              <Stack spacing={1.5}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('system:workers.defaultDepartment')}</InputLabel>
                  <Select
                    value={data.default_department_id ?? ''}
                    label={t('system:workers.defaultDepartment')}
                    onChange={(e) =>
                      set('default_department_id', e.target.value as string)
                    }
                  >
                    <MenuItem value="">
                      <em>{t('system:shared.none')}</em>
                    </MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('system:workers.defaultPosition')}</InputLabel>
                  <Select
                    value={data.default_position_id ?? ''}
                    label={t('system:workers.defaultPosition')}
                    onChange={(e) =>
                      set('default_position_id', e.target.value as string)
                    }
                  >
                    <MenuItem value="">
                      <em>{t('system:shared.none')}</em>
                    </MenuItem>
                    {positions.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.title || p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
          >
            <Box sx={{ px: 2.5, py: 1.25 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: '0.9rem' }}
              >
                {t('system:workers.cardEmployment')}
              </Typography>
            </Box>
            <CardContent sx={{ px: 2.5, pt: 0.75, pb: 2 }}>
              <Stack spacing={1.5}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('system:workers.defaultLocation')}</InputLabel>
                  <Select
                    value={data.default_location_id ?? ''}
                    label={t('system:workers.defaultLocation')}
                    onChange={(e) =>
                      set('default_location_id', e.target.value as string)
                    }
                  >
                    <MenuItem value="">
                      <em>{t('system:shared.none')}</em>
                    </MenuItem>
                    {locations.map((l) => (
                      <MenuItem key={l.id} value={l.id}>
                        {l.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('system:workers.defaultEmploymentType')}</InputLabel>
                  <Select
                    value={data.default_employment_type_id ?? ''}
                    label={t('system:workers.defaultEmploymentType')}
                    onChange={(e) =>
                      set('default_employment_type_id', e.target.value as string)
                    }
                  >
                    <MenuItem value="">
                      <em>{t('system:shared.none')}</em>
                    </MenuItem>
                    {employmentTypes.map((et) => (
                      <MenuItem key={et.id} value={et.id}>
                        {et.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
};

export default Workers;
